import { atom } from "jotai";
import { isValidUUID } from "../utils/uuid";
import { zslAtom } from "./zslAtom";

/**
 * Checks if a string is a valid standard ID (alphanumeric characters)
 * This accepts any mix of letters and numbers with standard length
 */
const isValidStandardID = (id: string): boolean => {
  // Check minimum length (adjust as needed)
  if (id?.length < 20) return false;

  // Accept standard alphanumeric IDs (a-z, A-Z, 0-9)
  const standardIdRegex = /^[0-9a-zA-Z]{20,}$/;
  return standardIdRegex.test(id);
};

/**
 * Checks if an ID is valid (either UUID or standard alphanumeric ID)
 */
const isValidID = (id: string): boolean =>
  isValidUUID(id) || isValidStandardID(id);

/**
 * Checks if any assets in tables/interfaces/zaps/chatbots have a non-valid id
 * Returns true if draft nodes are detected, false otherwise
 */
export const zslHasDraftNodesAtom = atom((get) => {
  const zsl = get(zslAtom);

  if (!zsl) {
    return false;
  }
  // Check tables
  if (zsl.tables && zsl.tables.length > 0) {
    const hasDraftTable = zsl?.tables?.some((table) => !isValidID(table.id));
    if (hasDraftTable) {
      return true;
    }
  }

  // Check interfaces
  if (zsl.interfaces && zsl.interfaces.length > 0) {
    const hasDraftInterface = zsl.interfaces.some(
      (interface_) => !isValidID(interface_.id),
    );
    if (hasDraftInterface) {
      return true;
    }
  }

  // Check zaps
  if (zsl.zaps && zsl.zaps.length > 0) {
    const hasDraftZap = zsl.zaps.some((zap) => !isValidID(zap.id));
    if (hasDraftZap) {
      return true;
    }
  }

  // Check chatbots
  if (zsl.chatbots && zsl.chatbots.length > 0) {
    const hasDraftChatbot = zsl.chatbots.some(
      (chatbot) => !isValidID(chatbot.id),
    );
    if (hasDraftChatbot) {
      return true;
    }
  }

  return false;
});

zslHasDraftNodesAtom.debugLabel = "zslHasDraftNodesAtom";
